/**
 * ScoreDisplay 컴포넌트
 * 레트로 게임 스타일의 점수 표시
 * - 세그먼트 디스플레이 느낌
 * - 점수 증가 애니메이션
 * - 도트 스타일 숫자
 */

import React, { useEffect, useState } from 'react';

/**
 * ScoreDisplay Props
 */
interface ScoreDisplayProps {
  /**
   * 현재 점수
   */
  score: number;

  /**
   * 최고 점수 (선택사항)
   */
  highScore?: number;

  /**
   * 애니메이션 활성화 여부
   */
  animated?: boolean;
}

/**
 * ScoreDisplay 컴포넌트
 * 레트로 게임 HUD 스타일의 점수 표시
 */
export const ScoreDisplay: React.FC<ScoreDisplayProps> = ({
  score,
  highScore,
  animated = true,
}) => {
  const [displayScore, setDisplayScore] = useState(score);
  const [isIncreasing, setIsIncreasing] = useState(false);

  /**
   * 점수 변경 시 애니메이션
   */
  useEffect(() => {
    if (!animated) {
      setDisplayScore(score);
      return;
    }

    if (score > displayScore) {
      setIsIncreasing(true);
      const duration = 300; // 애니메이션 지속 시간 (ms)
      const steps = 10; // 애니메이션 단계 수
      const increment = (score - displayScore) / steps;
      let current = displayScore;
      let step = 0;

      const timer = setInterval(() => {
        step++;
        current += increment;

        if (step >= steps) {
          setDisplayScore(score);
          setIsIncreasing(false);
          clearInterval(timer);
        } else {
          setDisplayScore(Math.floor(current));
        }
      }, duration / steps);

      return () => clearInterval(timer);
    } else {
      setDisplayScore(score);
    }
  }, [score, animated]);

  /**
   * 숫자를 6자리로 포맷 (000000)
   */
  const formatScore = (num: number): string => {
    return num.toString().padStart(6, '0');
  };

  return (
    <div className="font-pixel">
      {/* 점수 라벨 */}
      <div className="text-[10px] text-gray-400 mb-0.5 tracking-wider">SCORE</div>

      {/* 점수 디스플레이 - 세그먼트 디스플레이 스타일 */}
      <div
        className={`
          bg-[#0f380f]
          px-2
          py-1
          text-[#9bbc0f]
          text-sm md:text-xl
          tracking-widest
          transition-all
          ${isIncreasing ? 'scale-105' : 'scale-100'}
        `}
        style={{
          border: '2px solid #306230',
          boxShadow: 'inset 0 0 10px rgba(155,188,15,0.3), 0 2px 0 #306230',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {formatScore(displayScore)}
      </div>
    </div>
  );
};
